import { useEffect, useRef } from 'react'
import type { StatusBannerVariant } from '@metro/station-card'
import type { MetroAnalyticsReporter } from '../interaction/metroAnalyticsReporter'
import {
  lineStatusSeverityFromVariant,
  reportLineStatusViewed,
} from '../analytics/disruptionEvents'

/**
 * Host-side `line_status_viewed` wiring (catalog §G-10) for the station-card
 * `StatusBanner`. Fires the cross-platform event ONCE per fresh badge
 * appearance: when the selected station's card shows a status banner, with
 * `line_id` = the station's line, `severity` mapped from the banner variant,
 * `surface: 'station'`.
 *
 * Once-per-appearance de-dupe (catalog: "de-dupe per badge instance; re-emit
 * on a fresh appear, not on recompose/scroll-back-into-view within the same
 * mount"): the latch ref holds the `line:variant` key we last emitted for.
 * A recompose with the same line+variant is a no-op; the banner disappearing
 * (`variant` → null) resets the latch so the next appearance re-emits; a
 * different line or a changed severity is a fresh appearance and re-emits.
 *
 * Why a host hook (not in `@metro/station-card`): the package is
 * domain-agnostic — it renders the banner from a `StatusBannerVariant` and
 * knows nothing of line-schema ids or the analytics backend. The host owns the
 * `line_id` (the selected station's line) and injects the reporter, same
 * pattern as `useStationCardAnalytics`.
 */
export interface LineStatusViewedAnalyticsInput {
  /**
   * The line-schema id of the currently-selected station (e.g.
   * `line-victoria`), or null when no station / no line is resolved. Never a
   * naptanId.
   */
  readonly lineId: string | null
  /**
   * The status-banner variant currently shown for the selected station, or
   * null when the card shows no status banner (no disruption). Drives the
   * mapped severity + the appear/disappear de-dupe.
   */
  readonly variant: StatusBannerVariant | null
  /** Project reporter (host injects `realMetroAnalyticsReporter`). */
  readonly reporter: MetroAnalyticsReporter
}

export function useLineStatusViewedAnalytics(input: LineStatusViewedAnalyticsInput): void {
  const { lineId, variant, reporter } = input
  const latchRef = useRef<string | null>(null)
  useEffect(() => {
    // No badge shown (no status, or no resolved line) → reset so the next
    // genuine appearance emits afresh.
    if (variant === null || lineId === null) {
      latchRef.current = null
      return
    }
    const key = `${lineId}:${variant}`
    if (latchRef.current === key) return // same badge instance, recompose — no re-emit
    latchRef.current = key
    reportLineStatusViewed(reporter, {
      lineId,
      severity: lineStatusSeverityFromVariant(variant),
      surface: 'station',
    })
  }, [lineId, variant, reporter])
}
